import type {TextProps, TitleProps} from '@mantine/core';
import {Text, Title as MantineTitle} from '@mantine/core';

/**
 * Preset text styles (`ContentHeader`, `Subtitle`, `SectionTitle`, `ContentTitle`, `Paragraph`, `Subtext`) taking a `text` prop.
 * Plain body text and h1 headings use the `Text` / `Title` pass-through shims.
 * @registry Heading/body text presets — ContentHeader (h2) through ContentTitle (h5), plus Paragraph/Subtext body styles.
 */

interface TitlePropsExtended extends Omit<TitleProps, 'children'> {
  text?: string;
}

interface TextPropsExtended extends Omit<TextProps, 'children'> {
  text?: string;
}

const ContentHeader = ({text, ...props}: TitlePropsExtended) => {
  return (
    <MantineTitle order={2} fz={{base: 'h3', md: 'h2'}} {...props}>
      {text}
    </MantineTitle>
  );
};

const Subtitle = ({text, ...props}: TitlePropsExtended) => {
  return (
    <MantineTitle order={3} {...props}>
      {text}
    </MantineTitle>
  );
};

const SectionTitle = ({text, ...props}: TitlePropsExtended) => {
  return (
    <MantineTitle order={4} mb="xs" {...props}>
      {text}
    </MantineTitle>
  );
};

const ContentTitle = ({text, ...props}: TitlePropsExtended) => {
  return (
    <MantineTitle order={5} mb={0} {...props}>
      {text}
    </MantineTitle>
  );
};

const Paragraph = ({text, ...props}: TextPropsExtended) => {
  return (
    <Text mb="md" {...props}>
      {text}
    </Text>
  );
};

const Subtext = ({text, ...props}: TextPropsExtended) => {
  return (
    <Text mb="md" size="sm" c="dimmed" {...props}>
      {text}
    </Text>
  );
};

export {
  ContentHeader,
  ContentTitle,
  Paragraph,
  SectionTitle,
  Subtext,
  Subtitle,
};
